"use client";

import { useTranslations } from "next-intl";
import { Button } from "@artha/ui";
import type { ResumeReviewForm } from "../../personalization.types";
import { PERSONALIZATION_FIELD_CLASS_NAME } from "../../personalization.utils";

type ReviewStepProps = {
  form: ResumeReviewForm;
  isRequestingCode: boolean;
  onChange: (form: ResumeReviewForm) => void;
  onSubmit: () => void;
};

export function ReviewStep({
  form,
  isRequestingCode,
  onChange,
  onSubmit,
}: ReviewStepProps) {
  const t = useTranslations("profile.resumePersonalization");
  const fields = [
    ["name", t("review.name"), t("review.namePlaceholder"), true],
    ["email", t("review.email"), t("review.emailPlaceholder"), true],
    ["phone", t("review.phone"), t("review.phonePlaceholder"), false],
    ["titles", t("review.titles"), t("review.titlesPlaceholder"), true],
    ["skills", t("review.skills"), t("review.skillsPlaceholder"), true],
    ["keywords", t("review.keywords"), t("review.keywordsPlaceholder"), false],
  ] as const;

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {fields.map(([field, label, placeholder, isRequired]) => (
        <label key={field} className="block space-y-1.5">
          <span className="text-sm font-semibold text-[#344054]">
            {label}
            {isRequired ? <span className="text-[#9D1C1C] ml-0.5">*</span> : null}
          </span>
          <input
            type={field === "email" ? "email" : "text"}
            aria-label={label}
            required={isRequired}
            value={form[field]}
            placeholder={placeholder}
            className={PERSONALIZATION_FIELD_CLASS_NAME}
            onChange={(event) =>
              onChange({ ...form, [field]: event.target.value })
            }
          />
          {field === "titles" ||
          field === "skills" ||
          field === "keywords" ? (
            <span className="block text-xs text-[#667085]">
              {t("review.commaHint")}
            </span>
          ) : null}
        </label>
      ))}
      <Button
        type="submit"
        className="h-12 w-full rounded-[10px] bg-primary font-bold text-primary-foreground"
        disabled={isRequestingCode}
      >
        {isRequestingCode ? t("actions.sendingCode") : t("review.submitCta")}
      </Button>
    </form>
  );
}
